import React, { useState, useEffect } from 'react';
import { Layers, Plus, Edit2, Trash2, Users, RefreshCw, ShieldCheck, CheckCircle2 } from 'lucide-react';
import api from '../api/api';
import DataTable from './ui/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmContext';

export default function SenderPools() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  
  const [name, setName] = useState('');
  const [mailAccountIds, setMailAccountIds] = useState([]);
  const [accountConfigs, setAccountConfigs] = useState([]);
  const [rotationStrategy, setRotationStrategy] = useState('ROUND_ROBIN');
  const [isActive, setIsActive] = useState(true);
  
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const confirm = useConfirm();

  useEffect(() => {
    fetchPools();
    fetchAccounts();
  }, []);

  const fetchPools = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/sender-pools');
      setPools(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sender pools');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/api/mail-accounts');
      setAvailableAccounts(res.data?.accounts || []);
    } catch (err) {
      console.error('Failed to load accounts for pool selection', err);
    }
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setSelectedPool(null);
    setName('');
    setMailAccountIds([]);
    setAccountConfigs([]);
    setRotationStrategy('ROUND_ROBIN');
    setIsActive(true);
    setIsModalOpen(true);
  };

  const openEditModal = (pool) => {
    setIsEditing(true);
    setSelectedPool(pool);
    setName(pool.name);
    setMailAccountIds(pool.mailAccountIds?.map(a => a._id || a) || []);
    setAccountConfigs(pool.accountConfigs || []);
    setRotationStrategy(pool.rotationStrategy || 'ROUND_ROBIN');
    setIsActive(pool.isActive ?? true);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.warning("Pool Name is required.");
      return;
    }
    if (mailAccountIds.length === 0) {
      toast.warning("Select at least one sender for the pool.");
      return;
    }

    try {
      const payload = {
        name,
        mailAccountIds,
        accountConfigs,
        rotationStrategy,
        isActive
      };

      if (isEditing) {
        await api.put(`/api/sender-pools/${selectedPool._id}`, payload);
        toast.success("Sender pool updated.");
      } else {
        await api.post('/api/sender-pools', payload);
        toast.success("Sender pool created.");
      }
      setIsModalOpen(false);
      fetchPools();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save pool');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Pool?", "Are you sure? This won't delete the sender accounts, just the pool definition.");
    if (!ok) return;
    try {
      await api.delete(`/api/sender-pools/${id}`);
      fetchPools();
      toast.success("Pool deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete pool');
    }
  };

  const toggleAccount = (id) => {
    setMailAccountIds(prev => {
      const isRemoving = prev.includes(id);
      const next = isRemoving ? prev.filter(aid => aid !== id) : [...prev, id];
      
      setAccountConfigs(current => {
        if (!isRemoving) {
          // Adding new account, add default config if not exists
          if (!current.find(c => (c.accountId?._id || c.accountId) === id)) {
            return [...current, { accountId: id, dailyLimit: 50, isActive: true }];
          }
          return current;
        } else {
          // Removing account, remove config
          return current.filter(c => (c.accountId?._id || c.accountId) !== id);
        }
      });
      
      return next;
    });
  };

  const updateAccountLimit = (accountId, limit) => {
    setAccountConfigs(prev => prev.map(c => 
      (c.accountId?._id || c.accountId) === accountId ? { ...c, dailyLimit: parseInt(limit) || 0 } : c
    ));
  };

  const columns = [
    {
      header: 'Pool Composition',
      accessor: 'name',
      sortable: true,
      render: (pool) => (
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl flex items-center justify-center shrink-0 ${pool.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold tracking-tight text-foreground text-sm">{pool.name}</div>
            <div className="text-[11px] font-semibold text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> {pool.mailAccountIds?.length || 0} Senders Assigned
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Strategy',
      accessor: 'rotationStrategy',
      sortable: true,
      render: (pool) => (
        <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border bg-muted/30 text-muted-foreground border-border flex items-center w-fit">
          <RefreshCw className="w-3 h-3 mr-1.5" />
          {pool.rotationStrategy?.replace(/_/g, ' ')}
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'isActive',
      sortable: true,
      render: (pool) => (
        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border flex items-center w-fit ${pool.isActive ? 'bg-success/10 text-success border-success/20' : 'bg-muted/50 text-muted-foreground border-dashed border-border/50'}`}>
          {pool.isActive ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1 opacity-50" />}
          {pool.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: '_id',
      sortable: false,
      align: 'right',
      render: (pool) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openEditModal(pool)} className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors border border-transparent hover:border-primary/20"><Edit2 className="w-4 h-4" /></button>
          <button onClick={() => handleDelete(pool._id)} className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors border border-transparent hover:border-destructive/20"><Trash2 className="w-4 h-4" /></button>
        </div>
      )
    }
  ];

  return (
    <PageShell className="animate-in fade-in duration-500">
      <PageHeader
        title="Sender Pools"
        subtitle="Group multiple sender accounts for smart rotation and resilient deliverability management."
        icon={Layers}
        actions={
          <button
            onClick={openCreateModal}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-xl shadow-sm text-sm font-bold hover:bg-primary/90 flex items-center transition-all"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Pool
          </button>
        }
      />

      {error && <div className="bg-destructive/10 text-destructive text-sm font-medium p-4 rounded-xl border border-destructive/20">{error}</div>}

      <DataTable
        columns={columns}
        data={pools}
        loading={loading}
        pagination={{ page: 1, limit: 100, totalRecords: pools.length, totalPages: 1 }}
        emptyState={
          <EmptyState
            title="No Sender Pools"
            description="Aggregate your sender identities into pools for automatic rotation and load balancing."
            icon={Layers}
          />
        }
      />

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in zoom-in-95">
          <div className="bg-card rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden border border-border/40 flex flex-col max-h-[90vh]">
            <div className="px-8 py-6 border-b border-border bg-card/60 backdrop-blur-xl shrink-0">
              <h2 className="text-2xl font-black tracking-tight text-foreground">
                {isEditing ? 'Modify Sender Pool' : 'Initialize Sender Pool'}
              </h2>
            </div>
            
            <div className="p-8 space-y-6 overflow-y-auto">
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Pool Name</label>
                <input
                  type="text"
                  value={name}
                  required
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. North America Primary Pool"
                  className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                />
              </div>

              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Rotation Strategy</label>
                <Select value={rotationStrategy} onValueChange={setRotationStrategy}>
                  <SelectTrigger className="w-full bg-muted/20 border-2 border-border text-foreground shadow-sm rounded-2xl focus:border-primary font-bold h-14 px-5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-xl">
                    <SelectItem value="ROUND_ROBIN" className="font-bold">Round Robin (Sequential)</SelectItem>
                    <SelectItem value="FIRST_AVAILABLE" className="font-bold">First Available (Priority)</SelectItem>
                    <SelectItem value="LEAST_USED_TODAY" className="font-bold">Least Used (Load Balanced)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Assign Senders</label>
                <div className="max-h-80 overflow-y-auto border-2 border-border rounded-2xl p-4 space-y-3 bg-muted/5 shadow-inner text-foreground">
                  {availableAccounts.length === 0 ? (
                    <div className="py-8 text-center text-sm font-medium text-muted-foreground italic">No mail accounts connected.</div>
                  ) : (
                    availableAccounts.map(acc => {
                      const isSelected = mailAccountIds.includes(acc._id);
                      const config = accountConfigs.find(c => (c.accountId?._id || c.accountId) === acc._id);
                      
                      return (
                        <div
                          key={acc._id}
                          className={`flex flex-col p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border/50 bg-background opacity-60 hover:opacity-100 hover:bg-muted/50 cursor-pointer'}`}
                          onClick={(e) => {
                            if (e.target.tagName !== 'INPUT') toggleAccount(acc._id);
                          }}
                        >
                          <div className="flex items-center">
                            <div className={`w-5 h-5 rounded flex items-center justify-center border-2 mr-3 ${isSelected ? 'bg-primary border-primary transition-all scale-110' : 'border-muted-foreground/30'}`}>
                              {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                            </div>
                            <div className="flex-1">
                               <div className="flex items-center gap-2">
                                 <span className="font-bold text-xs">{acc.user}</span>
                                 <span className="text-[10px] font-black uppercase tracking-tighter opacity-50 px-1 rounded bg-muted border border-border">{acc.providerType}</span>
                               </div>
                               <div className="text-[10px] font-semibold text-muted-foreground">Original Cap: {acc.dailyLimit}</div>
                            </div>
                            {isSelected && (
                               <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2">
                                 <div className="flex flex-col items-end">
                                   <span className="text-[9px] font-black uppercase tracking-widest text-primary mb-1">Pool Limit</span>
                                   <input 
                                     type="number" 
                                     value={config?.dailyLimit || 50} 
                                     onChange={(e) => updateAccountLimit(acc._id, e.target.value)}
                                     onClick={(e) => e.stopPropagation()}
                                     className="w-20 h-9 bg-background border-2 border-primary/20 rounded-lg text-center font-bold text-xs focus:border-primary outline-none shadow-sm transition-all"
                                   />
                                 </div>
                               </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer p-4 rounded-2xl border-2 border-border w-full hover:border-primary/50 transition-colors">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  className="w-5 h-5 rounded-md border-2 border-muted-foreground/30 focus:ring-primary text-primary"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-foreground">Pool Active</span>
                  <span className="text-[10px] font-medium text-muted-foreground">Make this pool available for campaigns</span>
                </div>
              </label>
            </div>

            <div className="px-8 py-6 border-t border-border bg-muted/10 flex justify-end gap-3 shrink-0">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-3 text-[11px] font-black uppercase tracking-widest text-muted-foreground transition-all hover:text-foreground">Abort</button>
              <button
                onClick={handleSave}
                className="px-8 py-3 text-[11px] font-black uppercase tracking-widest text-white bg-primary rounded-xl shadow-lg transition-all active:scale-[0.98] hover:scale-[1.02]"
              >
                Save Pool Design
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
